import React from 'react';
import {useIntl} from 'react-intl';
import {PromptTarget} from '../state/useNovaPromptState';
import {NovaVariable} from '../shared/novaVariableLibrary';
import {NovaMcpTool, mcpToolToken} from '../shared/novaMcpTools';
import {NovaMcpToolsState} from '../shared/useNovaMcpTools';
import {NovaInputIssue, NovaIssueKind, validateInput} from '../shared/novaInputValidation';
import {InsertLibraryGroup, NovaInsertLibrary} from './NovaInsertLibrary';

function tokenSlug(token: string): string {
	return token.replace(/[{}]/g, '');
}

// A workflow's variables as the workflow declares them: groups in declared order, each variable with
// its declared description.
export interface StudioWorkflowVariable {
	token: string;
	description: string;
	isCustomerData: boolean;
}

export interface StudioWorkflowVariableGroup {
	name: string;
	variables: StudioWorkflowVariable[];
}

interface Props {
	variableGroups: StudioWorkflowVariableGroup[];
	// The workflow's ready reference files, each citable as a {file:...} variable.
	fileVariables: NovaVariable[];
	activeTarget: PromptTarget;
	onInsert: (token: string) => void;
	// The declaration read failed, so the declared variables are unknown rather than absent.
	loadFailed?: boolean;
	// The declaration has not been read yet, so the library claims no count and no emptiness.
	loading?: boolean;
	// Reads the declaration again after a failed read.
	onRetry?: () => void;
	// The prompt a click inserts into, shown as a pill in the header.
	targetLabel?: string;
}

// Every reference a workflow prompt may contain: its declared variables, its files and the MCP tool
// tokens. Prompt validation reads these, so none of them is reported as an unknown variable.
export function workflowReferences(
	variableGroups: StudioWorkflowVariableGroup[],
	fileVariables: NovaVariable[],
	mcpTools: NovaMcpTool[] = []
): NovaVariable[] {
	const declared = variableGroups.flatMap(group =>
		group.variables.map(
			(variable): NovaVariable => ({
				token: variable.token,
				scope: 'PROJECT_CONTEXT',
				description: variable.description,
				lifecycleState: 'ACTIVE',
			})
		)
	);
	const tools = mcpTools.map(
		(tool): NovaVariable => ({
			token: mcpToolToken(tool),
			scope: 'PROJECT_CONTEXT',
			description: tool.purpose,
			lifecycleState: 'ACTIVE',
		})
	);
	return [...declared, ...fileVariables, ...tools];
}

// The tokens a workflow allows in the User prompt only: every variable it declares as customer data,
// and every file.
export function workflowCustomerDataTokens(
	variableGroups: StudioWorkflowVariableGroup[],
	fileVariables: NovaVariable[]
): ReadonlySet<string> {
	return new Set([
		...variableGroups.flatMap(group =>
			group.variables.filter(variable => variable.isCustomerData).map(variable => variable.token)
		),
		...fileVariables.map(file => file.token),
	]);
}

const TOOL_TOKEN_PREFIX = '{tool:';

// The issues in one workflow prompt, checked against what the workflow declares and the MCP tools
// Forecast-MCP serves. Nothing the Studio could not read is reported as unknown: while the declaration
// failed, no reference is; while the tool list is loading or failed, no tool reference is. A typo
// cannot be told from a name that failed to load.
export function validateWorkflowInput(
	text: string,
	target: PromptTarget,
	variableGroups: StudioWorkflowVariableGroup[],
	fileVariables: NovaVariable[],
	declarationFailed: boolean,
	mcpTools: NovaMcpToolsState
): NovaInputIssue[] {
	const issues = validateInput(text, target, {
		offered: workflowReferences(variableGroups, fileVariables, mcpTools.tools),
		removed: [],
		customerDataTokens: workflowCustomerDataTokens(variableGroups, fileVariables),
	});
	const toolsKnown = mcpTools.status === 'loaded';
	return issues.filter(
		issue =>
			issue.kind !== NovaIssueKind.UnknownVariable ||
			(!declarationFailed && (toolsKnown || !issue.token?.startsWith(TOOL_TOKEN_PREFIX)))
	);
}

const FILES_GROUP_KEY = 'files';

// A key per declared group from its name, made unique: two names can reduce to one key ("Run params"
// and "Run-params"), and "Files" would take the files group's.
function groupKeys(names: string[]): string[] {
	const used = new Set([FILES_GROUP_KEY]);
	return names.map(name => {
		const base = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
		let key = base;
		for (let suffix = 2; used.has(key); suffix += 1) {
			key = `${base}-${suffix}`;
		}
		used.add(key);
		return key;
	});
}

// Renders the open workflow's declaration exactly: no variable, group or wording it does not
// declare. Customer data may only enter the User prompt, so while the System prompt or agent.md is the
// target every customer-data variable and every file is shown disabled with the reason; run
// parameters stay insertable everywhere.
export function NovaWorkflowVariableLibrary({
	variableGroups,
	fileVariables,
	activeTarget,
	onInsert,
	loadFailed = false,
	loading = false,
	onRetry,
	targetLabel,
}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const isStandingInput = activeTarget !== PromptTarget.User;
	const customerDataTip = formatMessage({id: 'nova.workflowVariables.customerDataTip'});
	const blockedReason = (isCustomerData: boolean): string | null =>
		isStandingInput && isCustomerData ? customerDataTip : null;

	const keys = groupKeys(variableGroups.map(group => group.name));
	const groups: InsertLibraryGroup[] = variableGroups.map((group, index) => ({
		key: keys[index],
		label: group.name,
		rows: group.variables.map(variable => ({
			key: variable.token,
			token: variable.token,
			description: variable.description,
			disabledReason: blockedReason(variable.isCustomerData),
			dataCy: `nova-variable-${tokenSlug(variable.token)}`,
			onInsert: () => onInsert(variable.token),
		})),
	}));
	if (fileVariables.length > 0) {
		groups.push({
			key: FILES_GROUP_KEY,
			label: formatMessage({id: 'nova.variableLibrary.groupFiles'}),
			rows: fileVariables.map(file => ({
				key: file.token,
				token: file.token,
				description: file.description,
				disabledReason: blockedReason(true),
				dataCy: `nova-variable-${tokenSlug(file.token)}`,
				onInsert: () => onInsert(file.token),
			})),
		});
	}
	const count = variableGroups.reduce((total, group) => total + group.variables.length, 0) + fileVariables.length;

	return (
		<NovaInsertLibrary
			dataCy="nova-variable-library"
			title={formatMessage({id: 'nova.variableLibrary.title'})}
			helper={formatMessage({id: 'nova.variableLibrary.hint'})}
			countLabel={
				loading
					? formatMessage({id: 'nova.workflowVariables.loading'})
					: loadFailed
					? formatMessage({id: 'nova.workflowVariables.countUnavailable'})
					: formatMessage({id: 'nova.variableLibrary.count'}, {count})
			}
			targetLabel={targetLabel}
			groups={groups}
			notice={loadFailed ? formatMessage({id: 'nova.workflowVariables.loadFailed'}) : undefined}
			noticeAction={
				loadFailed && onRetry ? {label: formatMessage({id: 'nova.insertLibrary.retry'}), onPress: onRetry} : undefined
			}
			emptyText={formatMessage({id: loading ? 'nova.workflowVariables.loading' : 'nova.workflowVariables.empty'})}
		/>
	);
}
